import { useCallback, useEffect, useState } from "react";
import { BookOpen, Notebook, Trash, Plus } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";

export default function CarePlanAccessPanel({ clientId, clientName }) {
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);
  const [denied, setDenied] = useState(false);
  const [sel, setSel] = useState("");
  const [viewPlan, setViewPlan] = useState(true);
  const [notebook, setNotebook] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/careplan-access")
      .then((r) => setData(r.data))
      .catch(() => setDenied(true));
    api.get("/users/nav-order").then((r) => setStaff(r.data.users || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (denied) return null;
  const grants = (data?.grants || []).filter((g) => g.client_id === clientId);
  const suggestions = (data?.suggestions || []).filter((s) => s.client_id === clientId);

  const grant = async () => {
    if (!sel) { toast.error("Pick a caregiver"); return; }
    setBusy(true);
    try {
      await api.post("/admin/careplan-access/grant", { user_id: sel, client_id: clientId, view_plan: viewPlan, notebook });
      toast.success("Access granted");
      setSel("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const update = async (g, patch) => {
    try {
      await api.post("/admin/careplan-access/grant", { user_id: g.user_id, client_id: g.client_id, view_plan: g.view_plan, notebook: g.notebook, ...patch });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const revoke = async (g) => {
    try {
      await api.post("/admin/careplan-access/revoke", { user_id: g.user_id, client_id: g.client_id });
      toast.success("Access revoked");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="careplan-access-panel">
      <p className="font-heading text-sm font-semibold text-stone-900">Care plan access — {clientName}</p>
      <p className="mt-0.5 text-xs text-stone-500">
        Per caregiver, per client. "View & sign" lets them read the care plan and sign off on it each shift.
        "Notebook" lets them see other caregivers' daily wrap-ups for this client. Access closes automatically if the caregiver separates.
      </p>
      {grants.length > 0 && (
        <div className="mt-3 divide-y divide-stone-100">
          {grants.map((g) => (
            <div key={g.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`grant-row-${g.user_id}`}>
              <p className="text-sm font-medium text-stone-800">{g.caregiver}
                {g.caregiver_status && g.caregiver_status !== "active" && <span className="ml-2 text-xs text-[#C0392B]">({g.caregiver_status} — access closed)</span>}
              </p>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-stone-600">
                  <input type="checkbox" checked={g.view_plan} onChange={(e) => update(g, { view_plan: e.target.checked })} data-testid={`grant-view-${g.user_id}`} />
                  <BookOpen size={13} /> View & sign
                </label>
                <label className="flex items-center gap-1.5 text-xs text-stone-600">
                  <input type="checkbox" checked={g.notebook} onChange={(e) => update(g, { notebook: e.target.checked })} data-testid={`grant-notebook-${g.user_id}`} />
                  <Notebook size={13} /> Notebook
                </label>
                <button onClick={() => revoke(g)} className="text-stone-400 hover:text-[#C0392B]" data-testid={`grant-revoke-${g.user_id}`}><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {suggestions.length > 0 && (
        <p className="mt-2 text-[11px] text-stone-400">
          From the schedule/assignments: {suggestions.map((s) => s.caregiver).join(", ")} — pick below to enable.
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-stone-100 pt-3">
        <select value={sel} onChange={(e) => setSel(e.target.value)} className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="grant-caregiver-select">
          <option value="">Assign a caregiver…</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-stone-600">
          <input type="checkbox" checked={viewPlan} onChange={(e) => setViewPlan(e.target.checked)} data-testid="grant-new-view" /> View & sign
        </label>
        <label className="flex items-center gap-1.5 text-xs text-stone-600">
          <input type="checkbox" checked={notebook} onChange={(e) => setNotebook(e.target.checked)} data-testid="grant-new-notebook" /> Notebook
        </label>
        <Button onClick={grant} disabled={busy || !sel} size="sm" className="rounded-full bg-primary text-primary-foreground" data-testid="grant-submit">
          <Plus size={14} className="mr-1" /> Enable access
        </Button>
      </div>
    </div>
  );
}
